import { useImage } from "@hooks/useImage";
import { useRoutes } from "@hooks/useRoutes";
import { useWindowSize } from "@hooks/useWindowSize";
import { ComponentProps, ResolvedLink } from "@ts/components";
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import SwiperCore from "swiper";
import {
  FeaturedCollectionContent,
  FeaturedCollectionFromQuery,
} from "./withFeaturedCollection";

type FeaturedCollectionCarouselInputProps = ComponentProps & {
  activeTab?: string;
  autoDelay?: string;
  featuredCollectionContents: FeaturedCollectionFromQuery[];
  _key?: string;
};

type FeaturedCollectionCarouselOutputProps = Omit<
  FeaturedCollectionCarouselInputProps,
  "featuredCollectionContents"
> & {
  collectionContents: {
    link: ResolvedLink;
    contents: FeaturedCollectionContent[];
  }[];
  changeDirection: () => void;
  isMD?: boolean;
  setSwiper: Dispatch<SetStateAction<SwiperCore>>;
};

export const withFeaturedCollectionCarousel =
  (Component: FC<FeaturedCollectionCarouselOutputProps>) =>
  ({
    name = "FeaturedCollectionCarousel",
    autoDelay,
    activeTab,
    featuredCollectionContents,
    _key,
  }: FeaturedCollectionCarouselInputProps) => {
    const { linkResolver } = useRoutes();
    const { getFluidImage } = useImage();
    const {
      windowSize: { isMD },
    } = useWindowSize();

    const [swiper, setSwiper] = useState<SwiperCore | undefined>();

    const featuredCollection = () => {
      if (featuredCollectionContents?.length > 1) {
        return featuredCollectionContents?.filter(
          ({ title }) => title === activeTab,
        );
      }

      return featuredCollectionContents;
    };

    const collectionContents = useMemo(
      () =>
        featuredCollection().map((content) => {
          const normalisedTitleLink = linkResolver(content?.titleLink);
          const normalisedFeaturedCollectionContents =
            content?.contents ?? content?.collectionContents;
          const normalisedContents = normalisedFeaturedCollectionContents?.map(
            (normalisedContent) => ({
              ...normalisedContent,
              _key: normalisedContent._key,
              link: linkResolver(normalisedContent?.buttonLink?.[0]),
            }),
          );

          return {
            link: normalisedTitleLink,
            contents: normalisedContents,
          };
        }),
      [featuredCollection, linkResolver, getFluidImage],
    );

    const onInit = (swiperElement) => {
      if (!!autoDelay) {
        return;
      }

      if (swiperElement) {
        swiperElement.activeIndex = 0;
        swiperElement.isBeginning = true;
        swiperElement.isEnd = false;

        swiper.navigation["$nextEl"].forEach((element) => {
          element.ariaDisabled = "false";
          element.classList.remove("swiper-button-disabled");
        });

        swiper.navigation["$prevEl"].forEach((element) => {
          element.ariaDisabled = "true";
          element.classList.add("swiper-button-disabled");
        });
      }
    };

    const handleDirectionChange = () => {
      if (!!autoDelay) {
        return;
      }

      if (swiper) {
        swiper.el.children[0].style.transitionDuration = "300ms";
      }
    };

    useEffect(() => {
      onInit(swiper);
    }, []);

    Component.displayName = name;
    return (
      <Component
        activeTab={activeTab}
        autoDelay={autoDelay}
        collectionContents={collectionContents}
        changeDirection={handleDirectionChange}
        isMD={isMD}
        _key={_key}
        setSwiper={setSwiper}
      />
    );
  };
